import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';
import {
    Zap,
    Plus,
    Pencil,
    Trash2,
    Copy,
    Play,
    Pause,
    BarChart3,
    MessageSquare,
    Users,
    Clock,
    Calendar,
    Send,
    Upload,
    Eye,
    Loader2,
    Gift,
    PartyPopper,
    UserPlus,
    RefreshCw,
    Megaphone,
    Cake,
    CheckCircle,
    XCircle,
    UserX
} from 'lucide-react';

// Automation types
const automationTypes = [
    { value: 'remerciement', label: '🎉 Remerciement', description: 'Après livraison', icon: PartyPopper },
    { value: 'bienvenue', label: '👋 Bienvenue', description: 'Nouveau client', icon: UserPlus },
    { value: 'relance', label: '🔄 Relance', description: 'Client inactif', icon: RefreshCw },
    { value: 'promotion', label: '🎁 Promotion', description: 'Offres spéciales', icon: Gift },
    { value: 'anniversaire', label: '🎂 Anniversaire', description: 'Fidélité client', icon: Cake },
    { value: 'personnalise', label: '📢 Personnalisé', description: 'Message libre', icon: Megaphone },
];

// Order statuses for triggers
const orderStatuses = [
    { value: 'nouvelle', label: 'Nouvelle' },
    { value: 'confirme', label: 'Confirmée' },
    { value: 'en_attente', label: 'En Attente' },
    { value: 'injoignable', label: 'Injoignable' },
    { value: 'expedie', label: 'Expédiée' },
    { value: 'livre', label: 'Livrée' },
    { value: 'annule', label: 'Annulée' },
    { value: 'retourne', label: 'Retournée' },
];

// Client segments
const clientSegments = [
    { value: 'all', label: 'Tous les clients' },
    { value: 'new', label: 'Nouveaux clients (1ère commande)' },
    { value: 'loyal', label: 'Clients fidèles (3+ commandes)' },
    { value: 'vip', label: 'Clients VIP (10+ commandes)' },
    { value: 'inactive', label: 'Clients inactifs (30+ jours)' },
];

// Days of week
const daysOfWeek = [
    { value: 'mon', label: 'Lun' },
    { value: 'tue', label: 'Mar' },
    { value: 'wed', label: 'Mer' },
    { value: 'thu', label: 'Jeu' },
    { value: 'fri', label: 'Ven' },
    { value: 'sat', label: 'Sam' },
    { value: 'sun', label: 'Dim' },
];

// Available variables for messages
const messageVariables = [
    { var: '{nom}', label: 'Nom client' },
    { var: '{produit}', label: 'Nom produit' },
    { var: '{n_commande}', label: 'N° commande' },
    { var: '{prix}', label: 'Prix total' },
    { var: '{code_promo}', label: 'Code promo' },
    { var: '{lien_tracking}', label: 'Lien avec tracking' },
];

// Empty automation template
const emptyAutomation = {
    name: '',
    type: 'remerciement',
    trigger_status: ['livre'],
    delay_value: 24,
    delay_unit: 'hours',
    days_of_week: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
    time_start: '09:00',
    time_end: '21:00',
    respect_quiet_hours: true,
    frequency: 'once', // 'once' or 'recurring'
    recurring_interval: 30,
    recurring_unit: 'days',
    targeting: 'all', // 'all', 'manual', 'segment'
    segment: 'all',
    manual_clients: [],
    exclude_blocked_clients: true, // Don't send to blocked clients
    message: `🎉 Merci {nom} !

Nous espérons que votre {produit} vous plaît !

Votre avis compte beaucoup pour nous ⭐

🎁 -10% sur votre prochaine commande: {code_promo}

{lien_tracking}

BBM 💛`,
    promo_code: 'MERCI10',
    is_active: false,
};

export default function Automatisations() {
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total_sent: 0, total_conversions: 0, conversion_rate: 0 });
    
    // Dialog states
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAutomation, setEditingAutomation] = useState(null);
    const [formData, setFormData] = useState(emptyAutomation);
    const [submitting, setSubmitting] = useState(false);
    
    // Client selection dialog
    const [clientDialogOpen, setClientDialogOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [selectedClients, setSelectedClients] = useState([]);
    const [clientSearch, setClientSearch] = useState('');
    const [loadingClients, setLoadingClients] = useState(false);
    
    // Preview dialog
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewMessage, setPreviewMessage] = useState('');
    
    // Stats dialog
    const [statsDialogOpen, setStatsDialogOpen] = useState(false);
    const [selectedAutomationStats, setSelectedAutomationStats] = useState(null);
    
    // Instant SMS dialog
    const [instantSmsDialogOpen, setInstantSmsDialogOpen] = useState(false);
    const [instantSmsData, setInstantSmsData] = useState({
        phone: '',
        message: '',
        clientName: ''
    });
    const [sendingSms, setSendingSms] = useState(false);
    const [instantSmsClientSearch, setInstantSmsClientSearch] = useState('');
    const [showClientPicker, setShowClientPicker] = useState(false);

    // Fetch automations
    const fetchAutomations = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/automations');
            setAutomations(response.data.automations || []);
            setStats(response.data.stats || { total_sent: 0, total_conversions: 0, conversion_rate: 0 });
        } catch (error) {
            console.error('Error fetching automations:', error);
            toast.error('Erreur lors du chargement des automatisations');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAutomations();
    }, [fetchAutomations]);

    // Fetch clients for manual selection
    const fetchClients = async () => {
        try {
            setLoadingClients(true);
            const response = await api.get('/clients');
            setClients(response.data || []);
        } catch (error) {
            console.error('Error fetching clients:', error);
            toast.error('Erreur lors du chargement des clients');
        } finally {
            setLoadingClients(false);
        }
    };

    // Open create/edit dialog
    const handleOpenDialog = (automation = null) => {
        if (automation) {
            setEditingAutomation(automation);
            setFormData({
                name: automation.name || '',
                type: automation.type || 'remerciement',
                trigger_status: automation.trigger_status || ['livre'],
                delay_value: automation.delay_value || 24,
                delay_unit: automation.delay_unit || 'hours',
                days_of_week: automation.days_of_week || ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
                time_start: automation.time_start || '09:00',
                time_end: automation.time_end || '21:00',
                respect_quiet_hours: automation.respect_quiet_hours !== false,
                frequency: automation.frequency || 'once',
                recurring_interval: automation.recurring_interval || 30,
                recurring_unit: automation.recurring_unit || 'days',
                targeting: automation.targeting || 'all',
                segment: automation.segment || 'all',
                manual_clients: automation.manual_clients || [],
                exclude_blocked_clients: automation.exclude_blocked_clients !== false,
                message: automation.message || emptyAutomation.message,
                promo_code: automation.promo_code || 'MERCI10',
                is_active: automation.is_active || false,
            });
            setSelectedClients(automation.manual_clients || []);
        } else {
            setEditingAutomation(null);
            setFormData(emptyAutomation);
            setSelectedClients([]);
        }
        setDialogOpen(true);
    };

    // Handle form field change
    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Handle trigger status toggle
    const handleStatusToggle = (status) => {
        setFormData(prev => {
            const current = prev.trigger_status || [];
            if (current.includes(status)) {
                return { ...prev, trigger_status: current.filter(s => s !== status) };
            } else {
                return { ...prev, trigger_status: [...current, status] };
            }
        });
    };

    // Handle day toggle
    const handleDayToggle = (day) => {
        setFormData(prev => {
            const current = prev.days_of_week || [];
            if (current.includes(day)) {
                return { ...prev, days_of_week: current.filter(d => d !== day) };
            } else {
                return { ...prev, days_of_week: [...current, day] };
            }
        });
    };

    // Insert variable into message
    const insertVariable = (variable) => {
        setFormData(prev => ({
            ...prev,
            message: prev.message + variable
        }));
    };

    // Generate preview message
    const handlePreview = () => {
        const preview = formData.message
            .replace('{nom}', 'Ahmed')
            .replace('{produit}', 'Sauvage Dior 100ml')
            .replace('{n_commande}', 'PA#12345')
            .replace('{prix}', '4500 DA')
            .replace('{code_promo}', formData.promo_code || 'PROMO10')
            .replace('{lien_tracking}', `www.parfumalgerie.shop?promo=${formData.promo_code || 'PROMO10'}`);
        setPreviewMessage(preview);
        setPreviewOpen(true);
    };

    // Open client selection dialog
    const handleOpenClientDialog = () => {
        fetchClients();
        setClientDialogOpen(true);
    };

    // Toggle client selection
    const toggleClientSelection = (clientId) => {
        setSelectedClients(prev => {
            if (prev.includes(clientId)) {
                return prev.filter(id => id !== clientId);
            } else {
                return [...prev, clientId];
            }
        });
    };

    // Confirm client selection
    const confirmClientSelection = () => {
        setFormData(prev => ({ ...prev, manual_clients: selectedClients }));
        setClientDialogOpen(false);
    };

    // Handle CSV import
    const handleCsvImport = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split('\n');
            const phones = [];
            
            lines.forEach(line => {
                const phone = line.trim().replace(/[^0-9+]/g, '');
                if (phone.length >= 9) {
                    phones.push(phone);
                }
            });

            if (phones.length > 0) {
                toast.success(`${phones.length} numéros importés`);
                setFormData(prev => ({
                    ...prev,
                    imported_phones: phones,
                    targeting: 'manual'
                }));
            } else {
                toast.error('Aucun numéro valide trouvé');
            }
        };
        reader.readAsText(file);
    };

    // Submit automation
    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            toast.error('Le nom est requis');
            return;
        }
        if (formData.trigger_status.length === 0) {
            toast.error('Sélectionnez au moins un statut déclencheur');
            return;
        }
        if (!formData.message.trim()) {
            toast.error('Le message est requis');
            return;
        }

        setSubmitting(true);
        try {
            const data = {
                ...formData,
                manual_clients: selectedClients,
            };

            if (editingAutomation) {
                await api.put(`/automations/${editingAutomation.id}`, data);
                toast.success('Automatisation mise à jour');
            } else {
                await api.post('/automations', data);
                toast.success('Automatisation créée');
            }
            
            setDialogOpen(false);
            fetchAutomations();
        } catch (error) {
            console.error('Error saving automation:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSubmitting(false);
        }
    };

    // Toggle automation active state
    const handleToggleActive = async (automation) => {
        try {
            await api.put(`/automations/${automation.id}`, {
                ...automation,
                is_active: !automation.is_active
            });
            toast.success(automation.is_active ? 'Automatisation désactivée' : 'Automatisation activée');
            fetchAutomations();
        } catch (error) {
            console.error('Error toggling automation:', error);
            toast.error('Erreur lors de la modification');
        }
    };

    // Delete automation
    const handleDelete = async (automation) => {
        if (!window.confirm(`Supprimer l'automatisation "${automation.name}" ?`)) {
            return;
        }

        try {
            await api.delete(`/automations/${automation.id}`);
            toast.success('Automatisation supprimée');
            fetchAutomations();
        } catch (error) {
            console.error('Error deleting automation:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    // Duplicate automation
    const handleDuplicate = (automation) => {
        const duplicated = {
            ...automation,
            name: `${automation.name} (copie)`,
            is_active: false,
        };
        delete duplicated.id;
        delete duplicated.created_at;
        delete duplicated.stats;
        setEditingAutomation(null);
        setFormData(duplicated);
        setSelectedClients(duplicated.manual_clients || []);
        setDialogOpen(true);
    };

    // View automation stats
    const handleViewStats = async (automation) => {
        try {
            const response = await api.get(`/automations/${automation.id}/stats`);
            setSelectedAutomationStats({
                automation,
                ...response.data
            });
            setStatsDialogOpen(true);
        } catch (error) {
            console.error('Error fetching stats:', error);
            toast.error('Erreur lors du chargement des statistiques');
        }
    };

    // Filter clients by search
    const filteredClients = clients.filter(client => {
        const search = clientSearch.toLowerCase();
        return (
            (client.full_name || '').toLowerCase().includes(search) ||
            (client.phone || '').includes(search) ||
            (client.email || '').toLowerCase().includes(search)
        );
    });

    // Filter clients for instant SMS
    const filteredInstantSmsClients = clients.filter(client => {
        const search = instantSmsClientSearch.toLowerCase();
        return (
            (client.full_name || '').toLowerCase().includes(search) ||
            (client.phone || '').includes(search)
        );
    });

    // Open instant SMS dialog
    const handleOpenInstantSms = () => {
        fetchClients();
        setInstantSmsData({ phone: '', message: '', clientName: '' });
        setInstantSmsClientSearch('');
        setShowClientPicker(false);
        setInstantSmsDialogOpen(true);
    };

    // Select client for instant SMS
    const handleSelectClientForSms = (client) => {
        setInstantSmsData({
            phone: client.phone,
            message: instantSmsData.message,
            clientName: client.full_name
        });
        setShowClientPicker(false);
        setInstantSmsClientSearch('');
    };

    // Send instant SMS
    const handleSendInstantSms = async () => {
        if (!instantSmsData.phone.trim()) {
            toast.error('Numéro de téléphone requis');
            return;
        }
        if (!instantSmsData.message.trim()) {
            toast.error('Message requis');
            return;
        }

        setSendingSms(true);
        try {
            const response = await api.post('/sms/send-instant', {
                phone: instantSmsData.phone,
                message: instantSmsData.message,
                client_name: instantSmsData.clientName || 'Client'
            });
            
            if (response.data.success) {
                toast.success('SMS envoyé avec succès !');
                setInstantSmsDialogOpen(false);
                setInstantSmsData({ phone: '', message: '', clientName: '' });
            } else {
                toast.error(response.data.error || 'Erreur lors de l\'envoi');
            }
        } catch (error) {
            console.error('Error sending SMS:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du SMS');
        } finally {
            setSendingSms(false);
        }
    };

    // Insert variable into instant SMS message
    const insertInstantSmsVariable = (variable) => {
        setInstantSmsData(prev => ({
            ...prev,
            message: prev.message + variable
        }));
    };

    // Get automation type info
    const getTypeInfo = (type) => {
        return automationTypes.find(t => t.value === type) || automationTypes[0];
    };

    return (
        <Layout>
            <div className="space-y-6 p-4 md:p-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Zap className="w-6 h-6 text-amber-500" />
                            Automatisations SMS
                        </h1>
                        <p className="text-muted-foreground">
                            Créez des campagnes SMS automatiques pour fidéliser vos clients
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button 
                            onClick={handleOpenInstantSms} 
                            variant="outline" 
                            className="gap-2 border-primary text-primary hover:bg-primary/10"
                            data-testid="instant-sms-btn"
                        >
                            <Send className="w-4 h-4" />
                            SMS Instantané
                        </Button>
                        <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="new-automation-btn">
                            <Plus className="w-4 h-4" />
                            Nouvelle Automatisation
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">SMS Envoyés</p>
                                    <p className="text-2xl font-bold">{stats.total_sent}</p>
                                </div>
                                <Send className="w-8 h-8 text-blue-500 opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Conversions</p>
                                    <p className="text-2xl font-bold">{stats.total_conversions}</p>
                                </div>
                                <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Taux de Conversion</p>
                                    <p className="text-2xl font-bold">{stats.conversion_rate.toFixed(1)}%</p>
                                </div>
                                <BarChart3 className="w-8 h-8 text-purple-500 opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Automations List */}
                <Card>
                    <CardHeader>
                        <CardTitle>Mes Automatisations</CardTitle>
                        <CardDescription>
                            {automations.length} automatisation(s) configurée(s)
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : automations.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Zap className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                <p>Aucune automatisation créée</p>
                                <p className="text-sm">Créez votre première automatisation pour fidéliser vos clients</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {automations.map((automation) => {
                                    const typeInfo = getTypeInfo(automation.type);
                                    const TypeIcon = typeInfo.icon;
                                    return (
                                        <Card key={automation.id} className="border">
                                            <CardContent className="p-4">
                                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className={`p-2 rounded-lg ${automation.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                                                            <TypeIcon className={`w-5 h-5 ${automation.is_active ? 'text-green-600' : 'text-gray-500'}`} />
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="font-semibold">{automation.name}</h3>
                                                                <Badge variant={automation.is_active ? 'default' : 'secondary'}>
                                                                    {automation.is_active ? 'Actif' : 'Inactif'}
                                                                </Badge>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground">
                                                                Déclencheur: {automation.trigger_status?.map(s => orderStatuses.find(os => os.value === s)?.label || s).join(', ')}
                                                                {' | '}
                                                                Délai: {automation.delay_value} {automation.delay_unit === 'hours' ? 'h' : 'j'}
                                                                {' | '}
                                                                {automation.days_of_week?.map(d => daysOfWeek.find(dw => dw.value === d)?.label).join(', ')}
                                                                {' '}
                                                                {automation.time_start}-{automation.time_end}
                                                            </p>
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                📈 {automation.stats?.sent || 0} envoyés | {automation.stats?.conversions || 0} conversions ({((automation.stats?.conversions || 0) / Math.max(1, automation.stats?.sent || 1) * 100).toFixed(1)}%)
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <Switch
                                                            checked={automation.is_active}
                                                            onCheckedChange={() => handleToggleActive(automation)}
                                                        />
                                                        <Button size="sm" variant="outline" onClick={() => handleViewStats(automation)}>
                                                            <BarChart3 className="w-4 h-4" />
                                                        </Button>
                                                        <Button size="sm" variant="outline" onClick={() => handleOpenDialog(automation)}>
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        <Button size="sm" variant="outline" onClick={() => handleDuplicate(automation)}>
                                                            <Copy className="w-4 h-4" />
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(automation)}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Create/Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                {editingAutomation ? 'Modifier l\'automatisation' : 'Nouvelle Automatisation'}
                            </DialogTitle>
                            <DialogDescription>
                                Configurez votre automatisation SMS pour fidéliser vos clients
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-6 py-4">
                            {/* Basic Info */}
                            <div className="space-y-4 p-4 rounded-lg bg-muted/30 border">
                                <Label className="text-base font-semibold">📝 Informations de base</Label>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nom de l'automatisation *</Label>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => handleFormChange('name', e.target.value)}
                                            placeholder="Ex: Remerciement Post-Livraison"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Type</Label>
                                        <Select
                                            value={formData.type}
                                            onValueChange={(v) => handleFormChange('type', v)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {automationTypes.map(type => (
                                                    <SelectItem key={type.value} value={type.value}>
                                                        {type.label} - {type.description}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            {/* Trigger */}
                            <div className="space-y-4 p-4 rounded-lg bg-blue-50 border border-blue-200">
                                <Label className="text-base font-semibold text-blue-900">🎯 Déclencheur</Label>
                                
                                <div className="space-y-2">
                                    <Label className="text-sm">Quand le statut de commande devient:</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {orderStatuses.map(status => (
                                            <Badge
                                                key={status.value}
                                                variant={formData.trigger_status?.includes(status.value) ? 'default' : 'outline'}
                                                className="cursor-pointer"
                                                onClick={() => handleStatusToggle(status.value)}
                                            >
                                                {formData.trigger_status?.includes(status.value) && <CheckCircle className="w-3 h-3 mr-1" />}
                                                {status.label}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm">Délai avant envoi</Label>
                                        <Input
                                            type="number"
                                            value={formData.delay_value}
                                            onChange={(e) => handleFormChange('delay_value', parseInt(e.target.value) || 0)}
                                            min="0"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm">Unité</Label>
                                        <Select
                                            value={formData.delay_unit}
                                            onValueChange={(v) => handleFormChange('delay_unit', v)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="hours">Heures</SelectItem>
                                                <SelectItem value="days">Jours</SelectItem>
                                                <SelectItem value="weeks">Semaines</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            {/* Schedule */}
                            <div className="space-y-4 p-4 rounded-lg bg-amber-50 border border-amber-200">
                                <Label className="text-base font-semibold text-amber-900">📅 Planification</Label>
                                
                                <div className="space-y-2">
                                    <Label className="text-sm">Jours d'envoi</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {daysOfWeek.map(day => (
                                            <Badge
                                                key={day.value}
                                                variant={formData.days_of_week?.includes(day.value) ? 'default' : 'outline'}
                                                className="cursor-pointer"
                                                onClick={() => handleDayToggle(day.value)}
                                            >
                                                {day.label}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm">Heure début</Label>
                                        <Input
                                            type="time"
                                            value={formData.time_start}
                                            onChange={(e) => handleFormChange('time_start', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm">Heure fin</Label>
                                        <Input
                                            type="time"
                                            value={formData.time_end}
                                            onChange={(e) => handleFormChange('time_end', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        checked={formData.respect_quiet_hours}
                                        onCheckedChange={(c) => handleFormChange('respect_quiet_hours', c)}
                                    />
                                    <Label className="text-sm">Respecter les heures calmes (22h-9h = pas d'envoi)</Label>
                                </div>

                                <div className="space-y-2 pt-2 border-t border-amber-200">
                                    <Label className="text-sm">Fréquence par client</Label>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="frequency"
                                                checked={formData.frequency === 'once'}
                                                onChange={() => handleFormChange('frequency', 'once')}
                                            />
                                            <Label className="text-sm font-normal">Une seule fois (ne plus recontacter ce client)</Label>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="frequency"
                                                checked={formData.frequency === 'recurring'}
                                                onChange={() => handleFormChange('frequency', 'recurring')}
                                            />
                                            <Label className="text-sm font-normal">Récurrent: tous les</Label>
                                            <Input
                                                type="number"
                                                value={formData.recurring_interval}
                                                onChange={(e) => handleFormChange('recurring_interval', parseInt(e.target.value) || 30)}
                                                className="w-20"
                                                disabled={formData.frequency !== 'recurring'}
                                            />
                                            <Select
                                                value={formData.recurring_unit}
                                                onValueChange={(v) => handleFormChange('recurring_unit', v)}
                                                disabled={formData.frequency !== 'recurring'}
                                            >
                                                <SelectTrigger className="w-24">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="days">jours</SelectItem>
                                                    <SelectItem value="weeks">semaines</SelectItem>
                                                    <SelectItem value="months">mois</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Targeting */}
                            <div className="space-y-4 p-4 rounded-lg bg-purple-50 border border-purple-200">
                                <Label className="text-base font-semibold text-purple-900">👥 Ciblage</Label>
                                
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="targeting"
                                            checked={formData.targeting === 'all'}
                                            onChange={() => handleFormChange('targeting', 'all')}
                                        />
                                        <Label className="text-sm font-normal">Tous les clients correspondant au déclencheur</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="targeting"
                                            checked={formData.targeting === 'segment'}
                                            onChange={() => handleFormChange('targeting', 'segment')}
                                        />
                                        <Label className="text-sm font-normal">Par segment:</Label>
                                        <Select
                                            value={formData.segment}
                                            onValueChange={(v) => handleFormChange('segment', v)}
                                            disabled={formData.targeting !== 'segment'}
                                        >
                                            <SelectTrigger className="w-64">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {clientSegments.map(seg => (
                                                    <SelectItem key={seg.value} value={seg.value}>{seg.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="targeting"
                                            checked={formData.targeting === 'manual'}
                                            onChange={() => handleFormChange('targeting', 'manual')}
                                        />
                                        <Label className="text-sm font-normal">Sélection manuelle</Label>
                                        {formData.targeting === 'manual' && (
                                            <div className="flex gap-2">
                                                <Button size="sm" variant="outline" onClick={handleOpenClientDialog}>
                                                    <Users className="w-4 h-4 mr-1" />
                                                    Sélectionner ({selectedClients.length})
                                                </Button>
                                                <Label className="cursor-pointer">
                                                    <Button size="sm" variant="outline" asChild>
                                                        <span>
                                                            <Upload className="w-4 h-4 mr-1" />
                                                            Importer CSV
                                                        </span>
                                                    </Button>
                                                    <input
                                                        type="file"
                                                        accept=".csv,.txt"
                                                        className="hidden"
                                                        onChange={handleCsvImport}
                                                    />
                                                </Label>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Exclude blocked clients option */}
                                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                                    <div className="flex items-center gap-2">
                                        <UserX className="w-4 h-4 text-red-600" />
                                        <div>
                                            <Label className="text-sm font-medium">Exclure les clients bloqués</Label>
                                            <p className="text-xs text-muted-foreground">Ne pas envoyer aux clients marqués comme bloqués</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={formData.exclude_blocked_clients}
                                        onCheckedChange={(checked) => handleFormChange('exclude_blocked_clients', checked)}
                                    />
                                </div>
                            </div>

                            {/* Message */}
                            <div className="space-y-4 p-4 rounded-lg bg-green-50 border border-green-200">
                                <Label className="text-base font-semibold text-green-900">💬 Message</Label>
                                
                                <div className="space-y-2">
                                    <Label className="text-sm">Code promo (optionnel)</Label>
                                    <Input
                                        value={formData.promo_code}
                                        onChange={(e) => handleFormChange('promo_code', e.target.value.toUpperCase())}
                                        placeholder="Ex: MERCI10"
                                        className="w-48"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm">Variables disponibles (cliquer pour insérer):</Label>
                                    <div className="flex flex-wrap gap-1">
                                        {messageVariables.map(v => (
                                            <Badge
                                                key={v.var}
                                                variant="outline"
                                                className="cursor-pointer hover:bg-green-100"
                                                onClick={() => insertVariable(v.var)}
                                            >
                                                {v.var}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm">Message *</Label>
                                    <Textarea
                                        value={formData.message}
                                        onChange={(e) => handleFormChange('message', e.target.value)}
                                        rows={8}
                                        placeholder="Écrivez votre message..."
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {formData.message.length} caractères
                                    </p>
                                </div>

                                <Button variant="outline" onClick={handlePreview}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    Aperçu
                                </Button>
                            </div>
                        </div>

                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button variant="secondary" onClick={handleSubmit} disabled={submitting}>
                                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Enregistrer (Brouillon)
                            </Button>
                            <Button onClick={() => { handleFormChange('is_active', true); handleSubmit(); }} disabled={submitting}>
                                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                <Play className="w-4 h-4 mr-2" />
                                Activer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Preview Dialog */}
                <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>👁 Aperçu du message</DialogTitle>
                            <DialogDescription>
                                Voici comment apparaîtra votre message
                            </DialogDescription>
                        </DialogHeader>
                        <div className="bg-gray-100 rounded-lg p-4 whitespace-pre-wrap font-mono text-sm">
                            {previewMessage}
                        </div>
                        <DialogFooter>
                            <Button onClick={() => setPreviewOpen(false)}>Fermer</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Client Selection Dialog */}
                <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[80vh]">
                        <DialogHeader>
                            <DialogTitle>👥 Sélectionner les clients</DialogTitle>
                            <DialogDescription>
                                {selectedClients.length} client(s) sélectionné(s)
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4">
                            <Input
                                placeholder="Rechercher par nom, téléphone ou email..."
                                value={clientSearch}
                                onChange={(e) => setClientSearch(e.target.value)}
                            />
                            
                            <div className="max-h-96 overflow-y-auto border rounded-lg">
                                {loadingClients ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin" />
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-12">
                                                    <Checkbox
                                                        checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                                                        onCheckedChange={(checked) => {
                                                            if (checked) {
                                                                setSelectedClients(filteredClients.map(c => c.id));
                                                            } else {
                                                                setSelectedClients([]);
                                                            }
                                                        }}
                                                    />
                                                </TableHead>
                                                <TableHead>Client</TableHead>
                                                <TableHead>Téléphone</TableHead>
                                                <TableHead>Commandes</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredClients.slice(0, 100).map(client => (
                                                <TableRow key={client.id}>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={selectedClients.includes(client.id)}
                                                            onCheckedChange={() => toggleClientSelection(client.id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{client.full_name || 'N/A'}</p>
                                                            <p className="text-xs text-muted-foreground">{client.email}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{client.phone}</TableCell>
                                                    <TableCell>{client.order_count || 0}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setClientDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button onClick={confirmClientSelection}>
                                Confirmer ({selectedClients.length})
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Stats Dialog */}
                <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle>📊 Statistiques</DialogTitle>
                            <DialogDescription>
                                {selectedAutomationStats?.automation?.name}
                            </DialogDescription>
                        </DialogHeader>
                        
                        {selectedAutomationStats && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <Card>
                                        <CardContent className="p-4 text-center">
                                            <p className="text-2xl font-bold">{selectedAutomationStats.sent || 0}</p>
                                            <p className="text-sm text-muted-foreground">SMS Envoyés</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-4 text-center">
                                            <p className="text-2xl font-bold">{selectedAutomationStats.conversions || 0}</p>
                                            <p className="text-sm text-muted-foreground">Conversions</p>
                                        </CardContent>
                                    </Card>
                                </div>
                                
                                <Card>
                                    <CardContent className="p-4 text-center">
                                        <p className="text-3xl font-bold text-green-600">
                                            {((selectedAutomationStats.conversions || 0) / Math.max(1, selectedAutomationStats.sent || 1) * 100).toFixed(1)}%
                                        </p>
                                        <p className="text-sm text-muted-foreground">Taux de Conversion</p>
                                    </CardContent>
                                </Card>

                                {selectedAutomationStats.recent_sends && selectedAutomationStats.recent_sends.length > 0 && (
                                    <div>
                                        <Label className="text-sm font-semibold">Derniers envois</Label>
                                        <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                                            {selectedAutomationStats.recent_sends.map((send, i) => (
                                                <div key={i} className="flex items-center justify-between text-sm p-2 bg-muted/30 rounded">
                                                    <span>{send.client_name}</span>
                                                    <span className="text-muted-foreground">{new Date(send.sent_at).toLocaleDateString()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <DialogFooter>
                            <Button onClick={() => setStatsDialogOpen(false)}>Fermer</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Instant SMS Dialog */}
                <Dialog open={instantSmsDialogOpen} onOpenChange={setInstantSmsDialogOpen}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Send className="w-5 h-5 text-blue-500" />
                                Envoyer SMS Instantané
                            </DialogTitle>
                            <DialogDescription>
                                Envoyez un SMS directement à un client
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4">
                            {/* Client Selection */}
                            <div className="space-y-2">
                                <Label>Destinataire</Label>
                                <div className="relative">
                                    <div className="flex gap-2">
                                        <div className="flex-1 relative">
                                            <Input
                                                value={instantSmsData.phone}
                                                onChange={(e) => setInstantSmsData(prev => ({ ...prev, phone: e.target.value }))}
                                                placeholder="Numéro de téléphone (ex: +213555123456)"
                                                className="pr-24"
                                            />
                                            {instantSmsData.clientName && (
                                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                                                    {instantSmsData.clientName.split(' ')[0]}
                                                </span>
                                            )}
                                        </div>
                                        <Button 
                                            type="button" 
                                            variant="outline"
                                            onClick={() => setShowClientPicker(!showClientPicker)}
                                        >
                                            <Users className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    
                                    {/* Client Picker Dropdown */}
                                    {showClientPicker && (
                                        <div className="absolute z-10 w-full mt-1 bg-card border rounded-lg shadow-lg">
                                            <div className="p-2 border-b">
                                                <Input
                                                    value={instantSmsClientSearch}
                                                    onChange={(e) => setInstantSmsClientSearch(e.target.value)}
                                                    placeholder="Rechercher un client..."
                                                    className="h-8"
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                {loadingClients ? (
                                                    <div className="p-4 text-center">
                                                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                                                    </div>
                                                ) : filteredInstantSmsClients.length === 0 ? (
                                                    <div className="p-4 text-center text-sm text-muted-foreground">
                                                        Aucun client trouvé
                                                    </div>
                                                ) : (
                                                    filteredInstantSmsClients.slice(0, 10).map((client) => (
                                                        <button
                                                            key={client.id}
                                                            className="w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center justify-between text-sm"
                                                            onClick={() => handleSelectClientForSms(client)}
                                                        >
                                                            <span>{client.full_name}</span>
                                                            <span className="text-muted-foreground">{client.phone}</span>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Message */}
                            <div className="space-y-2">
                                <Label>Message</Label>
                                <Textarea
                                    value={instantSmsData.message}
                                    onChange={(e) => setInstantSmsData(prev => ({ ...prev, message: e.target.value }))}
                                    placeholder="Écrivez votre message..."
                                    className="min-h-[100px]"
                                />
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-wrap gap-1">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => insertInstantSmsVariable('{nom}')}
                                        >
                                            + Nom
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => insertInstantSmsVariable('{prenom}')}
                                        >
                                            + Prénom
                                        </Button>
                                    </div>
                                    <span className="text-xs text-muted-foreground">
                                        {instantSmsData.message.length} caractères
                                    </span>
                                </div>
                            </div>
                            
                            {/* Preview */}
                            {instantSmsData.message && (
                                <div className="p-3 bg-muted/30 rounded-lg">
                                    <Label className="text-xs text-muted-foreground">Aperçu</Label>
                                    <p className="text-sm mt-1">
                                        {instantSmsData.message
                                            .replace('{nom}', instantSmsData.clientName || 'Client')
                                            .replace('{prenom}', (instantSmsData.clientName || 'Client').split(' ')[0])
                                        }
                                    </p>
                                </div>
                            )}
                        </div>

                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setInstantSmsDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleSendInstantSms} 
                                disabled={sendingSms || !instantSmsData.phone || !instantSmsData.message}
                                className="gap-2"
                            >
                                {sendingSms ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Envoi...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Envoyer SMS
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
